import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Tabs, Tab, CircularProgress, Link, Avatar, IconButton } from '@mui/material';
import * as LucideIcons from 'lucide-react';
import { ArrowLeft, Edit, Circle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNode, usePicklists, useUsers, useNodeUpdates } from '@lingaai/shared-hooks';
import { NODE_REGISTRY as NODE_TYPE_CONFIG } from '@lingaai/types/graph/nodes';
import { formatDate, formatDateTime, formatNumber } from '@lingaai/shared-utils';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';
import { PropertiesSection } from './PropertiesSection';
import { RelationshipsSection } from './RelationshipsSection';
import { SourcesSection } from './SourcesSection';
import { AttachmentsSection } from './AttachmentsSection';
import { ThreadsSection } from './ThreadsSection';
import { OrgChartSection } from './OrgChartSection';
import { NodeDrawer } from './NodeDrawer';
import { VehicleScenarioPage } from '../../pages/VehicleScenarioPage';

// Helper to get Lucide icon by name
function getIcon(iconName: string): React.ReactNode {
  const icons = LucideIcons as unknown as Record<string, React.ComponentType<{ size: number; strokeWidth: number }>>;
  const IconComponent = icons[iconName];
  if (!IconComponent) {
    return <Circle size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />;
  }
  return <IconComponent size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />;
}

interface NodeDetailViewProps {
  type: string;
}

/**
 * Get display name for a node based on its type
 * Different node types use different fields as their "name"
 */
function getNodeDisplayName(nodeType: string, props: Record<string, unknown>): string {
  const nodeConfig = NODE_TYPE_CONFIG[nodeType];
  const field = nodeConfig?.formFields[0]?.key || 'name';
  return (props[field] as string) || 'Untitled';
}

interface DisplayProp {
  key: string;
  label: string;
  value: string;
  isUrl: boolean;
  isGroup: boolean;
  isRichText: boolean;
  newLine: boolean;
  groupItems?: { label: string; value: string }[];
}

/**
 * Get displayable props (excludes title field and image URLs)
 * Handles URLs (*_url suffix) and nested objects (groups)
 * Looks up picklist labels when available
 */
function getDisplayProps(
  props: Record<string, unknown>,
  nodeType: string,
  fieldPicklistMap: Record<string, string>,
  picklistLabelMap: Record<string, Record<string, string>>,
  fieldTypeMap: Record<string, string>,
  fieldFormatMap: Record<string, string | undefined>,
  userNameMap: Record<string, string>
): DisplayProp[] {
  const nodeConfig = NODE_TYPE_CONFIG[nodeType];
  const displayField = nodeConfig?.formFields[0]?.key || 'name';
  const result: DisplayProp[] = [];
  // Iterate over formFields to preserve order from config
  const formFields = nodeConfig?.formFields || [];
  for (const field of formFields) {
    const k = field.key;
    const v = props[k];
    if (k === displayField || k === 'name' || k.endsWith('_image_url') || k === 'source_raw_data_id' || k === 'source' || v === null || v === undefined || v === '') continue;
    const label = field.label || k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    if (typeof v === 'object' && !Array.isArray(v)) {
      // Nested object - group
      const groupItems = Object.entries(v as Record<string, unknown>)
        .filter(([, gv]) => gv !== null && gv !== undefined && gv !== '')
        .map(([gk, gv]) => ({
          label: gk.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          value: String(gv),
        }));
      if (groupItems.length > 0) {
        result.push({ key: k, label, value: '', isUrl: false, isGroup: true, isRichText: false, newLine: field.newLine || false, groupItems });
      }
    } else {
      // Look up picklist label if available
      let displayValue = String(v);
      const picklistName = fieldPicklistMap[k];
      if (picklistName && typeof v === 'string') {
        displayValue = picklistLabelMap[picklistName]?.[v] ?? displayValue;
      }
      // Format datetime/date fields based on field type
      const fieldType = fieldTypeMap?.[k];
      if (fieldType === 'datetime' && typeof v === 'string') {
        displayValue = formatDateTime(v);
      } else if (fieldType === 'date' && typeof v === 'string') {
        displayValue = formatDate(v);
      } else if (fieldType === 'user' && typeof v === 'string') {
        displayValue = userNameMap[v] ?? v;
      } else if (fieldType === 'number' && typeof v === 'number') {
        displayValue = formatNumber(v, fieldFormatMap[k]);
      }
      result.push({
        key: k,
        label,
        value: displayValue,
        isUrl: k.endsWith('_url') || fieldType === 'url',
        isGroup: false,
        isRichText: fieldType === 'richtext',
        newLine: field.newLine || false,
      });
    }
  }
  return result;
}

export function NodeDetailView({ type }: NodeDetailViewProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [updatedFields, setUpdatedFields] = useState<Set<string>>(new Set());

  const { data: node, isLoading, error } = useNode(id);
  const { data: allPicklists } = usePicklists();
  const { data: allUsers } = useUsers();

  // Subscribe to real-time updates for this node
  useNodeUpdates(id, (changedFields) => {
    console.log('[NodeDetailView] Fields updated:', changedFields);
    setUpdatedFields(new Set(changedFields));
    // Clear the highlight after animation completes (3 blinks × 0.6s = 1.8s)
    setTimeout(() => setUpdatedFields(new Set()), 2000);
  });

  const nodeConfig = NODE_TYPE_CONFIG[type];

  // Build picklist lookup map
  const picklistLabelMap = useMemo(() => {
    const map: Record<string, Record<string, string>> = {};
    if (allPicklists) {
      for (const picklist of allPicklists) {
        const picklistMap: Record<string, string> = {};
        const items = picklist.items as Array<{ id: string; label: string }>;
        for (const item of items) {
          picklistMap[item.id] = item.label;
        }
        map[picklist.name] = picklistMap;
      }
    }
    return map;
  }, [allPicklists]);

  // Build field-to-picklist map
  const fieldPicklistMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (nodeConfig) {
      for (const field of nodeConfig.formFields) {
        if (field.picklistName) {
          map[field.key] = field.picklistName;
        }
      }
    }
    return map;
  }, [nodeConfig]);

  // Build field-to-type map
  const fieldTypeMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (nodeConfig) {
      for (const field of nodeConfig.formFields) {
        map[field.key] = field.type;
      }
    }
    return map;
  }, [nodeConfig]);

  // Build field-to-format map
  const fieldFormatMap = useMemo(() => {
    const map: Record<string, string | undefined> = {};
    if (nodeConfig) {
      for (const field of nodeConfig.formFields) {
        map[field.key] = field.format;
      }
    }
    return map;
  }, [nodeConfig]);

  // Build user name lookup map
  const userNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (allUsers) {
      for (const user of allUsers) {
        map[user.id] = user.full_name || user.email;
      }
    }
    return map;
  }, [allUsers]);

  if (!nodeConfig) {
    return <Typography color="error">Unknown node type: {type}</Typography>;
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !node) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{nodeConfig.displayName} not found</Typography>
      </Box>
    );
  }

  const handleBack = () => {
    navigate(-1);
  };

  const displayProps = getDisplayProps(node.props, type, fieldPicklistMap, picklistLabelMap, fieldTypeMap, fieldFormatMap, userNameMap);
  const imageUrl = Object.entries(node.props).find(([k]) => k.endsWith('_image_url'))?.[1] as string | undefined;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper
        sx={{
          p: 2.5,
          mb: 2,
          background: (theme) =>
            theme.palette.mode === 'dark' ? 'linear-gradient(135deg, rgba(45,55,72,1) 0%, rgba(26,32,44,1) 100%)' : 'linear-gradient(135deg, rgba(247,250,252,1) 0%, rgba(237,242,247,1) 100%)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            mb: displayProps.length > 0 ? 2 : 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
            {imageUrl && <Avatar src={imageUrl} alt={node.props.name as string} sx={{ width: 48, height: 48, flexShrink: 0 }} variant="rounded" />}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', color: nodeConfig.color, flexShrink: 0 }}>{getIcon(nodeConfig.icon)}</Box>
              <Typography variant="h5" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {getNodeDisplayName(type, node.props)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            <IconButton onClick={handleBack} size="small" sx={{ borderRadius: 1 }}>
              <ArrowLeft size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
            </IconButton>
            <IconButton onClick={() => setDrawerOpen(true)} size="small" sx={{ borderRadius: 1 }}>
              <Edit size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
            </IconButton>
          </Box>
        </Box>
        {displayProps.length > 0 && (
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {displayProps.map((prop) =>
              prop.isGroup ? (
                <Box key={prop.key} sx={{ flexBasis: prop.newLine ? '100%' : 'auto' }}>
                  <Typography variant="caption" color="text.secondary">
                    {prop.label}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {prop.groupItems?.map((g) => g.value).join(', ')}
                  </Typography>
                </Box>
              ) : (
                <Box
                  key={prop.key}
                  sx={{
                    flexBasis: prop.newLine ? '100%' : 'auto',
                    '@keyframes highlightBlink': {
                      '0%, 100%': { backgroundColor: 'transparent' },
                      '50%': { backgroundColor: 'rgba(76, 175, 80, 0.2)' },
                    },
                    animation: updatedFields.has(prop.key) ? 'highlightBlink 0.6s ease-in-out 3' : 'none',
                    borderRadius: 1,
                    px: updatedFields.has(prop.key) ? 1 : 0,
                    mx: updatedFields.has(prop.key) ? -1 : 0,
                    transition: 'padding 0.2s, margin 0.2s',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {prop.label}
                  </Typography>
                  {prop.isUrl ? (
                    <Link href={prop.value} target="_blank" rel="noopener noreferrer" sx={{ display: 'block', fontWeight: 500 }}>
                      {prop.value.replace(/^https?:\/\//, '')}
                    </Link>
                  ) : prop.isRichText ? (
                    <Box
                      sx={{
                        '& p': { m: 0, minHeight: '1em', '&:empty::before': { content: '"\\00a0"' } },
                        '& ul, & ol': { m: 0, pl: 2, mb: 1 },
                        '& li': { mb: 0.5 },
                      }}
                      dangerouslySetInnerHTML={{ __html: prop.value }}
                    />
                  ) : (
                    <Typography variant="body1" fontWeight={500}>
                      {prop.value}
                    </Typography>
                  )}
                </Box>
              )
            )}
          </Box>
        )}
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Details" />
          <Tab label="Relationships" />
          {nodeConfig.hasAttachments && <Tab label="Attachments" />}
          {nodeConfig.hasThreads && <Tab label="Threads" />}
          {nodeConfig.hasOrgChart && <Tab label="Org Chart" />}
          <Tab label="Sources" />
          {type === 'Vehicle' && <Tab label="Scenarios" />}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && <PropertiesSection nodeId={node.id} nodeType={type} />}
      {activeTab === 1 && <RelationshipsSection nodeId={node.id} nodeType={type} />}
      {activeTab === 2 && nodeConfig.hasAttachments && <AttachmentsSection nodeId={node.id} />}
      {activeTab === 2 && !nodeConfig.hasAttachments && nodeConfig.hasOrgChart && <OrgChartSection organizationId={node.id} />}
      {activeTab === 2 && !nodeConfig.hasAttachments && !nodeConfig.hasOrgChart && <SourcesSection nodeId={node.id} nodeType={type} />}
      {activeTab === 3 && nodeConfig.hasAttachments && nodeConfig.hasThreads && <ThreadsSection nodeId={node.id} />}
      {activeTab === 3 && nodeConfig.hasAttachments && !nodeConfig.hasThreads && nodeConfig.hasOrgChart && <OrgChartSection organizationId={node.id} />}
      {activeTab === 3 && nodeConfig.hasAttachments && !nodeConfig.hasThreads && !nodeConfig.hasOrgChart && <SourcesSection nodeId={node.id} nodeType={type} />}
      {activeTab === 3 && !nodeConfig.hasAttachments && nodeConfig.hasThreads && <ThreadsSection nodeId={node.id} />}
      {activeTab === 3 && !nodeConfig.hasAttachments && nodeConfig.hasOrgChart && <SourcesSection nodeId={node.id} nodeType={type} />}
      {activeTab === 4 && nodeConfig.hasAttachments && nodeConfig.hasThreads && nodeConfig.hasOrgChart && <OrgChartSection organizationId={node.id} />}
      {activeTab === 4 && nodeConfig.hasAttachments && nodeConfig.hasThreads && !nodeConfig.hasOrgChart && <SourcesSection nodeId={node.id} nodeType={type} />}
      {activeTab === 4 && nodeConfig.hasAttachments && !nodeConfig.hasThreads && nodeConfig.hasOrgChart && <SourcesSection nodeId={node.id} nodeType={type} />}
      {activeTab === 4 && nodeConfig.hasAttachments && !nodeConfig.hasThreads && !nodeConfig.hasOrgChart && type === 'Vehicle' && <VehicleScenarioPage />}
      {activeTab === 4 && !nodeConfig.hasAttachments && nodeConfig.hasThreads && nodeConfig.hasOrgChart && <SourcesSection nodeId={node.id} nodeType={type} />}
      {activeTab === 4 && !nodeConfig.hasAttachments && nodeConfig.hasThreads && !nodeConfig.hasOrgChart && <SourcesSection nodeId={node.id} nodeType={type} />}
      {activeTab === 5 && nodeConfig.hasAttachments && nodeConfig.hasThreads && nodeConfig.hasOrgChart && <SourcesSection nodeId={node.id} nodeType={type} />}
      {activeTab === 5 && nodeConfig.hasAttachments && nodeConfig.hasThreads && !nodeConfig.hasOrgChart && type === 'Vehicle' && <VehicleScenarioPage />}
      {activeTab === 6 && nodeConfig.hasAttachments && nodeConfig.hasThreads && nodeConfig.hasOrgChart && type === 'Vehicle' && <VehicleScenarioPage />}

      <NodeDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} nodeType={type} mode="edit" node={node} />
    </Box>
  );
}
